import type { Attachment, ChatSession } from './types';




/**
 * Sentinel id for the client-only "New Chat" placeholder. Server-issued
 * session ids are UUID/ULID-shaped, so the literal string `'pending'`
 * cannot collide with a real session id. Keep this as a plain string so
 * it threads cleanly through `activeSessionId: string | null`.
 */
export const PENDING_ID = 'pending' as const;

export function makeRequestId(): string {
  // Prefer native crypto.randomUUID when available (jsdom may lack it; tests
  // polyfill it).
  if (
    typeof crypto !== 'undefined' &&
    typeof (crypto as any).randomUUID === 'function'
  ) {
    return (crypto as any).randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Coerce the server's `attachments` field on a thread response into the
 * `Attachment[]` shape used by the FarChat presentation layer. The home
 * thread payload may carry either a `string[]` (filenames only) or an
 * `Attachment[]`-shaped array; we accept both.
 */
export function coerceAttachments(raw: unknown): Attachment[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item: any): Attachment | null => {
      if (typeof item === 'string') {
        return { filename: item, size: 0, mime_type: '' };
      }
      if (item && typeof item === 'object' && typeof item.filename === 'string') {
        return {
          filename: String(item.filename),
          size: typeof item.size === 'number' ? item.size : 0,
          mime_type: typeof item.mime_type === 'string' ? item.mime_type : '',
        };
      }
      return null;
    })
    .filter((a): a is Attachment => a !== null);
}

/**
 * Field-wise equality for two session lists. Used so a background refresh
 * that returns the SAME data reuses the previous array reference instead of
 * allocating a new one. A stable reference means the rail's `sessions` prop
 * identity is unchanged, so `SessionsRail`'s memoized rows (and its `now`
 * memo) are genuine no-ops — a poll tick with identical data triggers no
 * re-render and therefore no flash. The ONLY field changes that matter for
 * the rail are `title`, `last_activity_at`, and ordering (by `session_id`),
 * so we compare those; `message_count` is also compared since it's cheap and
 * a row could legitimately change without a title change.
 */
export function sessionsEqual(a: ChatSession[], b: ChatSession[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const x = a[i];
    const y = b[i];
    if (
      x.session_id !== y.session_id ||
      x.title !== y.title ||
      x.last_activity_at !== y.last_activity_at ||
      x.created_at !== y.created_at ||
      x.message_count !== y.message_count ||
      x.activity?.operation_id !== y.activity?.operation_id ||
      x.activity?.kind !== y.activity?.kind ||
      x.activity?.label !== y.activity?.label ||
      x.activity?.started_at !== y.activity?.started_at ||
      x.activity?.expires_at !== y.activity?.expires_at
      || x.completion?.completion_id !== y.completion?.completion_id
      || x.completion?.operation_id !== y.completion?.operation_id
      || x.completion?.state !== y.completion?.state
      || x.completion?.label !== y.completion?.label
      || x.completion?.completed_at !== y.completion?.completed_at
      || x.completion?.unseen !== y.completion?.unseen
    ) {
      return false;
    }
  }
  return true;
}
